import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Drawer, Input, Row, Table, message,
} from 'antd';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import DrawerFooter from '../../common/containers/DrawerFooter';
import StepsContainer from '../../common/containers/StepsContainer';
import colors from '../../constants/Colors';
import { includesTerm } from '../../helpers/helpers';
import ProjectBillingRateConfigurePanels from './ProjectBillingRateConfigurePanels';
import { 
  billingTypeDisplayName,
  templateToConfig,
  configToCreatePayload,
  getConfigureValidationError,
} from './ProjectBillingRateHelper';

export default function ProjectBillingRateAddFromTemplateDrawer({
  divisionId,
  visible,
  onClose,
  onSubmit,
}) {
  const templatesMap = useSelector((state) => state.projectBillingRates.templates);
  const users = useSelector((state) => state.users.users);
  const divisions = useSelector((state) => state.settings.divisions);
  const positions = useSelector((state) => state.settings.positionNames);
  const equipmentTypes = useSelector((state) => state.equipment.equipmentTypes);
  const equipments = useSelector((state) => state.equipment.equipment);

  const [currentStep, setCurrentStep] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [configsByTemplateId, setConfigsByTemplateId] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [errorTemplateIds, setErrorTemplateIds] = useState([]);
  const [collapseActiveKeys, setCollapseActiveKeys] = useState([]);

  const userOptions = useMemo(() => {
    const activeUsers = users.filter((user) => user.active);
    if (!divisionId) {
      return [];
    }
    const divisionUsers = divisions[divisionId]?.users ?? new Set();
    return activeUsers
      .filter((user) => divisionUsers.has(user.id))
      .map((user) => ({ label: user.name, value: user.id }));
  }, [users, divisions, divisionId]);

  const positionOptions = useMemo(
    () => positions.map((position) => ({ label: position.name, value: position.id })),
    [positions],
  );

  const equipmentTypeOptions = useMemo(
    () => equipmentTypes.map((equipmentType) => ({
      label: equipmentType.name,
      value: equipmentType.id,
    })),
    [equipmentTypes],
  );

  const equipmentOptions = useMemo(() => {
    if (!divisionId) {
      return [];
    }
    return equipments
      .filter((equipment) => equipment.active && equipment?.divisionIds?.includes(divisionId))
      .map((equipment) => ({
        label: equipment.name,
        value: equipment.id,
      }));
  }, [equipments, divisionId]);

  const allTemplates = useMemo(
    () => Object.values(templatesMap ?? {}),
    [templatesMap],
  );

  const filteredTemplates = useMemo(() => {
    if (!searchText) return allTemplates;
    return allTemplates.filter(
      (template) => includesTerm(template.name, searchText)
        || includesTerm(template.type, searchText),
    );
  }, [allTemplates, searchText]);

  useEffect(() => {
    if (!visible) return;
    setCurrentStep(0);
    setSelectedRowKeys([]);
    setSearchText('');
    setConfigsByTemplateId({});
    setSubmitting(false);
    setErrorTemplateIds([]);
    setCollapseActiveKeys([]);
  }, [visible]);

  const onSearchChanged = useCallback((event) => {
    setSearchText(event.target.value || '');
  }, []);

  const onSelectionChange = useCallback((newKeys) => {
    const visibleIds = new Set(filteredTemplates.map((template) => template.id));
    const keptSelection = selectedRowKeys.filter((rowKey) => !visibleIds.has(rowKey));
    setSelectedRowKeys([...keptSelection, ...newKeys]);
  }, [filteredTemplates, selectedRowKeys]);

  const updateConfig = useCallback((templateId, patch) => {
    setErrorTemplateIds((prev) => prev.filter((id) => id !== templateId));
    setConfigsByTemplateId((prevConfigs) => ({
      ...prevConfigs,
      [templateId]: {
        ...prevConfigs[templateId],
        ...patch,
      },
    }));
  }, []);

  const handleNext = useCallback(() => {
    if (selectedRowKeys.length === 0) return;
    setConfigsByTemplateId((prevConfigs) => {
      const newConfigs = { ...prevConfigs };
      selectedRowKeys.forEach((templateId) => {
        const template = templatesMap[templateId];
        if (!template) return;
        if (!newConfigs[templateId]) {
          newConfigs[templateId] = templateToConfig(template);
        }
      });
      Object.keys(newConfigs).forEach((templateId) => {
        if (!selectedRowKeys.includes(templateId)) delete newConfigs[templateId];
      });
      return newConfigs;
    });
    setErrorTemplateIds([]);
    setCollapseActiveKeys([]);
    setCurrentStep(1);
  }, [selectedRowKeys, templatesMap]);

  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setErrorTemplateIds([]);
      setCollapseActiveKeys([]);
    }
  }, [currentStep]);

  const handleSubmit = useCallback(async () => {
    const validationError = getConfigureValidationError(configsByTemplateId, selectedRowKeys);
    if (validationError) {
      message.error(validationError.message);
      setErrorTemplateIds([validationError.templateId]);
      setCollapseActiveKeys((prev) => Array.from(new Set([...prev, validationError.templateId])));
      return;
    }

    const payload = selectedRowKeys
      .map((templateId) => configsByTemplateId[templateId])
      .filter(Boolean)
      .map(configToCreatePayload);

    setSubmitting(true);
    try {
      const result = await onSubmit(payload);
      if (result?.success) {
        message.success(
          `Added ${payload.length} billing rates.`,
        );
        onClose();
      } else {
        message.error(result?.message || 'Could not add billing rates.');
        const ids = [...new Set((result?.invalidSourceTemplateIds || []).filter(Boolean))];
        if (ids.length) {
          setErrorTemplateIds(ids);
          setCollapseActiveKeys((prev) => Array.from(new Set([...prev, ...ids])));
        } else {
          setErrorTemplateIds([]);
        }
      }
    } catch {
      // should be handled above
    } finally {
      setSubmitting(false);
    }
  }, [configsByTemplateId, onClose, onSubmit, selectedRowKeys]);

  const selectionColumns = useMemo(() => [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (cellType) => billingTypeDisplayName(cellType),
    },
  ], []);

  const rowSelection = useMemo(() => ({
    selectedRowKeys,
    onChange: onSelectionChange,
    columnWidth: 48,
    getCheckboxProps: () => ({ className: 'ontraccr-checkbox' }),
  }), [selectedRowKeys, onSelectionChange]);

  const steps = useMemo(() => [
    {
      title: 'Select templates',
      content: (
        <div>
          <Input.Search
            type="search"
            placeholder="Search templates"
            allowClear
            style={{
              flex: 1,
              height: 40,
              borderRadius: 8,
              borderColor: colors.GRAY_400,
              marginBottom: 8,
            }}
            onChange={onSearchChanged}
            value={searchText}
          />
          <Table
            size="small"
            rowKey="id"
            columns={selectionColumns}
            dataSource={filteredTemplates}
            pagination={false}
            rowSelection={rowSelection}
            scroll={{ y: 400 }}
            locale={{ emptyText: 'No templates available' }}
          />
          <div style={{ marginTop: 8, color: colors.GRAY_800, fontSize: 13 }}>
            {selectedRowKeys.length} template(s) selected
          </div>
        </div>
      ),
    },
    {
      title: 'Configure rates',
      content: (
        <ProjectBillingRateConfigurePanels
          selectedRowKeys={selectedRowKeys}
          templatesMap={templatesMap}
          configsByTemplateId={configsByTemplateId}
          updateConfig={updateConfig}
          userOptions={userOptions}
          positionOptions={positionOptions}
          equipmentTypeOptions={equipmentTypeOptions}
          equipmentOptions={equipmentOptions}
          errorTemplateIds={errorTemplateIds}
          collapseActiveKeys={collapseActiveKeys}
          onCollapseActiveKeysChange={setCollapseActiveKeys}
        />
      ),
    },
  ], [
    filteredTemplates,
    onSearchChanged,
    searchText,
    selectionColumns,
    rowSelection,
    selectedRowKeys,
    templatesMap,
    configsByTemplateId,
    updateConfig,
    userOptions,
    positionOptions,
    equipmentTypeOptions,
    equipmentOptions,
    errorTemplateIds,
    collapseActiveKeys,
  ]);

  if (!visible) return null;

  return (
    <Drawer
      title="Add rates from templates"
      visible={visible}
      onClose={onClose}
      width={720}
      destroyOnClose
      keyboard={false}
      bodyStyle={{ paddingBottom: 88 }}
    >
      <StepsContainer
        currentStep={currentStep}
        steps={steps}
        containerStyle={{
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
        }}
      />
      <DrawerFooter style={{ padding: '16px 24px', borderColor: colors.GRAY_200 }}>
        <Row justify="space-between">
          <div>
            {currentStep > 0 && (
              <OnTraccrButton
                title="Back"
                type="cancel"
                onClick={handleBack}
              />
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <OnTraccrButton
              title="Cancel"
              type="cancel"
              onClick={onClose}
            />
            {currentStep === 0 ? (
              <OnTraccrButton
                title="Next"
                onClick={handleNext}
                disabled={selectedRowKeys.length === 0}
              />
            ) : (
              <OnTraccrButton
                title="Submit"
                onClick={handleSubmit}
                loading={submitting}
              />
            )}
          </div>
        </Row>
      </DrawerFooter>
    </Drawer>
  );
}

ProjectBillingRateAddFromTemplateDrawer.propTypes = {
  divisionId: PropTypes.string.isRequired,
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};
